import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy } from "lucide-react";

const CATEGORIES = ["Grooming", "Behaviour", "Punctuality", "SOP Compliance"];

/** Phase C · Daily performance report — one form per staff / shift.
 *  Slider 0-100 per category. Overall is a simple mean until Phase D adds weightages. */
export default function DailyReport({ open, onOpenChange, outlets, defaultOutletId }) {
  const today = new Date().toISOString().slice(0,10);
  const [outletId, setOutletId] = useState(defaultOutletId || "");
  const [date, setDate] = useState(today);
  const [staff, setStaff] = useState([]);
  const [existing, setExisting] = useState([]);
  const [selected, setSelected] = useState(null);      // staff object
  const [shift, setShift] = useState("General");
  const [scores, setScores] = useState({});
  const [sched, setSched] = useState(""); const [actual, setActual] = useState("");
  const [reason, setReason] = useState(""); const [remarks, setRemarks] = useState("");
  useEffect(() => { if (defaultOutletId) setOutletId(defaultOutletId); }, [defaultOutletId]);

  useEffect(() => {
    if (!open) return;
    api.get("/staff").then(r => setStaff(r.data.filter(s => s.status === "Active")));
  }, [open]);
  const reloadExisting = async () => {
    if (!outletId || !date) return;
    try { const r = await api.get(`/daily-report?outlet_id=${outletId}&date=${date}`); setExisting(r.data); }
    catch { /* silent */ }
  };
  useEffect(() => { reloadExisting(); }, [outletId, date, open]);
  const outletStaff = staff.filter(s => !outletId || s.outlet_id === outletId);
  const scoreFor = (sid) => existing.find(r => r.staff_id === sid);

  const openFor = (s) => {
    setSelected(s); setShift("General");
    setScores(Object.fromEntries(CATEGORIES.map(c => [c, 80])));
    setSched(""); setActual(""); setReason(""); setRemarks("");
  };
  const copyPrev = async () => {
    if (!selected) return;
    // Fetch this staff's most recent report
    try { const r = await api.get(`/daily-report?outlet_id=${outletId}&staff_id=${selected.id}`);
      if (r.data.length === 0) return toast.error("No previous report to copy");
      const last = r.data[0];
      setScores(last.scores || {}); setShift(last.shift || "General");
      setSched(last.scheduled_time || ""); setActual(last.actual_time || "");
      setReason(last.reason || ""); setRemarks(last.remarks || "");
      toast.success(`Copied from ${last.date}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    if (!selected) return;
    try {
      const late = sched && actual ? Math.max(0, Math.round((new Date(`${date}T${actual}`) - new Date(`${date}T${sched}`)) / 60000)) : 0;
      await api.post("/daily-report", {
        staff_id: selected.id, outlet_id: outletId, date, shift, scores,
        scheduled_time: sched, actual_time: actual, late_by_mins: late,
        attendance: "Present", reason, remarks,
      });
      toast.success("Report saved");
      setSelected(null); reloadExisting();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-4xl max-h-[92vh] overflow-y-auto" style={{ color: "#2b1a0d" }} data-testid="daily-report-root">
        <DialogHeader><DialogTitle className="font-display text-2xl">Daily / Shift-wise Report</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-3 items-end mb-3 pb-3 border-b" style={{borderColor:"#F3E7C4"}}>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
            <select data-testid="dr-outlet" value={outletId} onChange={e => setOutletId(e.target.value)} className="h-9 px-2 rounded border bg-white min-w-[200px]" style={{borderColor:"#E4D6A8"}}>
              <option value="">— pick outlet —</option>
              {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Date</label>
            <input data-testid="dr-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
          </div>
        </div>

        {!outletId ? (
          <div className="rounded-2xl border border-dashed p-10 text-center text-sm" style={{borderColor:"#E4D6A8", color:"#7a6349"}}>Pick an outlet to see today's staff.</div>
        ) : selected ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div><b>{selected.full_name}</b> <span className="opacity-60 text-xs">· {selected.designation || "—"}</span></div>
              <Button size="sm" variant="outline" data-testid="dr-copy-prev" onClick={copyPrev}><Copy size={12} className="mr-1" /> Copy previous</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Shift</label>
                <select data-testid="dr-shift" value={shift} onChange={e => setShift(e.target.value)} className="h-9 w-full px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}}>
                  <option>Opening</option><option>General</option><option>Closing</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Scheduled</label>
                  <Input type="time" value={sched} onChange={e => setSched(e.target.value)} data-testid="dr-sched" />
                </div>
                <div>
                  <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Actual</label>
                  <Input type="time" value={actual} onChange={e => setActual(e.target.value)} data-testid="dr-actual" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {CATEGORIES.map(c => (
                <div key={c} className="flex items-center gap-3">
                  <div className="w-32 text-sm font-medium">{c}</div>
                  <input data-testid={`dr-score-${c}`} type="range" min="0" max="100" step="1" value={scores[c] ?? 80}
                    onChange={e => setScores(s => ({...s, [c]: Number(e.target.value)}))} className="flex-1" />
                  <b className="w-12 text-right">{scores[c] ?? 80}%</b>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Late reason (optional)" value={reason} onChange={e => setReason(e.target.value)} data-testid="dr-reason" />
              <Input placeholder="Manager remarks" value={remarks} onChange={e => setRemarks(e.target.value)} data-testid="dr-remarks" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setSelected(null)}>Back</Button>
              <Button data-testid="dr-save" onClick={save} className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722]">Save report</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>Tap a staff to record today's report</div>
            {outletStaff.length === 0 ? <div className="text-xs text-center py-4" style={{color:"#7a6349"}}>No active staff assigned.</div> : outletStaff.map(s => {
              const done = scoreFor(s.id);
              return (
                <button key={s.id} data-testid={`dr-pick-${s.id}`} onClick={() => openFor(s)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded border hover:bg-[#FFF8E7]" style={{borderColor:"#E4D6A8"}}>
                  <div><b>{s.full_name}</b> <span className="opacity-60 text-xs">{s.designation || "—"}</span></div>
                  {done ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓ {done.overall}%</span> : <span className="text-xs opacity-60">not filed</span>}
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
